import React, { ReactElement, useEffect } from 'react'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'

import Autocomplete from '@mui/material/Autocomplete'
import Box from '@mui/material/Box'
import TextField, { TextFieldProps } from '@mui/material/TextField'

import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  fetchTimeZones,
  selectTimeZones,
  selectTimeZonesStatus,
} from '../../settings/settings.slice'

dayjs.extend(utc)
dayjs.extend(timezone)

const TimeZoneDropdown = ({
  sx,
  name = 'timeZone',
  value,
  label = 'Time Zone',
  variant = 'standard',
  onChange,
  error = false,
  bgDefault,
}: Omit<TextFieldProps, 'onChange'> & {
  onChange: (val: string) => void
  bgDefault?: boolean
}): ReactElement => {
  let dispatch = useAppDispatch()

  let timeZones = useAppSelector(selectTimeZones)
  let timeZonesStatus = useAppSelector(selectTimeZonesStatus)

  useEffect(() => {
    if (timeZonesStatus === 'uninitialized') {
      dispatch(fetchTimeZones())
    }
  }, [timeZonesStatus])

  let defaultZones = [
    'New York',
    'Chicago',
    'Denver',
    'Los Angeles',
    'Anchorage',
    'Honolulu',
  ]
  let filteredZones = timeZones.filter((zone) =>
    defaultZones.some((city) => zone.displayName.includes(city)),
  )

  let guess = dayjs.tz.guess()
  let guessData = timeZones.find((zone) => zone.name === guess)
  let guessedZone = filteredZones.find(
    (z) => guessData?.utcOffset === z.utcOffset,
  )

  let findZoneName = (displayName: string) =>
    filteredZones.find((zone) => zone.displayName === displayName)?.name || ''

  // eslint-disable-next-line fp/no-mutating-methods
  let options = filteredZones
    .map((zone) => zone.displayName || zone.name)
    .reverse()
    .sort((a, b) =>
      a === (guessedZone?.displayName || guessedZone?.name)
        ? -1
        : a.localeCompare(b),
    )

  return (
    <Autocomplete
      sx={{
        ...(sx || {}),
        bgcolor: !bgDefault ? 'background.paperDark' : undefined,
      }}
      value={
        filteredZones.find((zone) => zone.name === value)?.displayName || ''
      }
      fullWidth
      onChange={(_, val) => onChange(findZoneName(val as any))}
      options={options}
      renderOption={(props, option) => {
        // eslint-disable-next-line fp/no-rest-parameters
        let { key, ...optionProps } = props
        return (
          <Box
            key={key}
            component="li"
            sx={
              option === (guessedZone?.displayName || guessedZone?.name)
                ? { color: 'primary.light' }
                : undefined
            }
            {...optionProps}
          >
            {option}
          </Box>
        )
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          variant={variant}
          name={name}
          error={error}
        />
      )}
    />
  )
}

export default TimeZoneDropdown
